import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PUBLIC_API_URL, awsDeployUrl, azureDeployUrl } from "../lib/env";
import type {
  Cloud,
  Connector,
  CreateConnectorInput,
  CreateKeyResult,
  NamingMode,
  TestConnectorResult,
} from "../lib/types";
import { CommandBlock } from "../components/CommandBlock";
import { CopyButton } from "../components/CopyButton";
import { NamingSelect } from "../components/NamingSelect";

/**
 * Guided "New connection" wizard. Step 1 picks the cloud, which drives the rest:
 *   Azure: Cloud → Account → Grant access (Deploy to Azure) → Destination → Key
 *   AWS:   Cloud → Account (role + external id) → Destination → Key
 * The standalone Connectors / Keys pages remain the manage / add-another surface.
 */

type StepKey = "cloud" | "account" | "grant" | "destination" | "key";
const STEPS: Record<Cloud, { key: StepKey; label: string }[]> = {
  azure: [
    { key: "cloud", label: "Cloud" },
    { key: "account", label: "Account" },
    { key: "grant", label: "Grant access" },
    { key: "destination", label: "Destination" },
    { key: "key", label: "Upload key" },
  ],
  aws: [
    { key: "cloud", label: "Cloud" },
    { key: "account", label: "Account" },
    { key: "grant", label: "Grant access" },
    { key: "destination", label: "Destination" },
    { key: "key", label: "Upload key" },
  ],
};

function randomToken(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

function generateExternalId(): string {
  // CloudFormation's ExternalId param requires MinLength 16; the prefix + UUID
  // clears that comfortably.
  return `blobship-${randomToken()}`;
}

export function ConnectWizardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [cloud, setCloud] = useState<Cloud | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  // Before a cloud is chosen, show Azure's shape as the skeleton.
  const steps = STEPS[cloud ?? "azure"];
  const step = steps[stepIdx]!.key;

  const tenancies = useQuery({
    queryKey: ["tenancies"],
    queryFn: () => api.azure.tenancies(),
    // A just-consented tenancy is "pending" until Graph propagates its SP — poll
    // until every tenancy is ready, then stop.
    refetchInterval: (query) =>
      query.state.data?.some((t) => !t.ready) ? 3000 : false,
  });

  // Returning from Microsoft admin consent: land on the Azure account step with
  // the freshly connected tenancy preselected (wizard state is lost across the
  // full-page consent round-trip, so we resume here).
  const [params, setParams] = useSearchParams();
  const consent = params.get("consent");
  useEffect(() => {
    if (consent === "ok") {
      setCloud("azure");
      setStepIdx(1);
      tenancies.refetch();
      const t = params.get("tenant");
      if (t) {
        // preselect once the list arrives
        const id = tenancies.data?.find((x) => x.azureTenantId === t)?.id;
        if (id) setTenancyId(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent, tenancies.data]);

  // account — shared + per-cloud
  const [displayName, setDisplayName] = useState("");
  const [tenancyId, setTenancyId] = useState(""); // azure — chosen connected tenancy
  const [storageAccount, setStorageAccount] = useState(""); // azure
  const [roleArn, setRoleArn] = useState(""); // aws (manual paste fallback)
  const [region, setRegion] = useState("eu-west-2"); // aws
  const [showAwsManual, setShowAwsManual] = useState(false); // aws fallback toggle
  const externalId = useMemo(() => generateExternalId(), []); // aws
  const [connector, setConnector] = useState<Connector | null>(null);

  // destination — live container names (Azure, post-grant) power a datalist;
  // free text still works if listing isn't available.
  const containerOptions = useQuery({
    queryKey: ["containers", connector?.id],
    queryFn: () => api.connectors.containers(connector!.id),
    enabled: Boolean(connector),
  });
  const [container, setContainer] = useState("");
  const [prefix, setPrefix] = useState("");
  const [isolate, setIsolate] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [naming, setNaming] = useState<NamingMode>("unique"); // advanced
  const [maxMb, setMaxMb] = useState(""); // advanced
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const [probeAttemptCount, setProbeAttemptCount] = useState(0);

  // key
  const [keyResult, setKeyResult] = useState<CreateKeyResult | null>(null);

  // Access probe — on the key step we poll the connector test with backoff.
  //
  // Timing matters here and we got it wrong before: Azure documents up to TEN
  // minutes for a role assignment to reach the storage data plane (and says not
  // to investigate before 30). The old loop gave up after ~3 min and the UI then
  // showed a hard "Access denied", so a first-time Azure customer lost this race
  // essentially every time — see Aurora's first test. We now poll for ~15 min at
  // a steady 10s cadence once warmed up, and only stop early when the API tells
  // us the verdict is final (`retryable: false`).
  const accessProbe = useQuery({
    queryKey: ["accessProbe", connector?.id, destinationId],
    queryFn: async () => {
      const result = await api.connectors.test(connector!.id);
      if (!result.ok && result.diagnosis.retryable) {
        setProbeAttemptCount((n) => n + 1);
      }
      return result;
    },
    enabled:
      step === "key" && Boolean(connector) && Boolean(destinationId) && !keyResult,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.ok) return false; // access confirmed → stop polling
      // A non-retryable verdict (wrong scope, missing container, our own
      // misconfig) will never change by waiting — stop and show the action.
      if (data && !data.diagnosis.retryable) return false;
      if (q.state.dataUpdateCount >= 90) return false; // ~15 min ceiling
      // Ramp 2s → 10s, then hold: frequent enough to feel live, cheap enough to
      // sustain for the full propagation window.
      return Math.min(2000 * 2 ** Math.max(0, q.state.dataUpdateCount - 1), 10000);
    },
  });

  // AWS grant step — poll the connector until the CloudFormation auto-register
  // callback fills in the role ARN (matched on the connector id, so refresh-safe).
  const awsRole = useQuery({
    queryKey: ["awsRole", connector?.id],
    queryFn: () => api.connectors.get(connector!.id),
    enabled: step === "grant" && cloud === "aws" && Boolean(connector),
    refetchInterval: (q) => (q.state.data?.awsRoleArn ? false : 4000),
  });
  const awsRoleRegistered = Boolean(awsRole.data?.awsRoleArn);

  function next() {
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  }
  function back() {
    setStepIdx((i) => Math.max(i - 1, 0));
  }
  // Picking the cloud IS the action — go straight to Account, no Continue click.
  function chooseCloud(c: Cloud) {
    setCloud(c);
    setStepIdx(1);
  }

  const selectedTenancy = tenancies.data?.find((t) => t.id === tenancyId) ?? null;
  const accessConfirmed = Boolean(accessProbe.data?.ok);
  // AWS calls it a bucket, Azure a container — label the destination accordingly.
  const storeNoun = cloud === "aws" ? "bucket" : "container";
  const StoreNoun = cloud === "aws" ? "Bucket" : "Container";

  // Access verified on the destination step → mint the key automatically the
  // moment we land on the key step. If access couldn't be confirmed, we leave
  // the manual "Create my key" button instead (see the key step below).
  useEffect(() => {
    if (step === "key" && accessConfirmed && !keyResult && createKey.isIdle) {
      createKey.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, accessConfirmed, keyResult]);

  const createConnector = useMutation({
    mutationFn: () => {
      const input: CreateConnectorInput =
        cloud === "aws"
          ? {
              cloud: "aws",
              displayName,
              // Only send a manually-pasted ARN; the Launch-Stack flow leaves it
              // blank and the callback fills it in (matched on the connector id).
              ...(roleArn ? { roleArn } : {}),
              externalId,
              region,
            }
          : {
              cloud: "azure",
              displayName,
              tenantId: selectedTenancy?.azureTenantId ?? "",
              storageAccount,
            };
      return api.connectors.create(input);
    },
    onSuccess: (c) => {
      setConnector(c);
      qc.invalidateQueries({ queryKey: ["connectors"] });
      next();
    },
  });

  const createDestination = useMutation({
    mutationFn: async () => {
      const dest = await api.destinations.create({
        connectorId: connector!.id,
        bucketOrContainer: container,
        keyPrefix: prefix || undefined,
        isolateByUser: isolate,
        naming,
        maxBytes: maxMb ? Number(maxMb) * 1024 * 1024 : undefined,
      });
      setDestinationId(dest.id);
      setProbeAttemptCount(0);
      qc.invalidateQueries({ queryKey: ["destinations"] });
      return dest;
    },
    onSuccess: () => next(),
  });

  const createKey = useMutation({
    mutationFn: () =>
      api.keys.create({
        destinationId: destinationId!,
        direction: "put",
        mode: "persistent",
        label: `${displayName || storageAccount || "connection"} key`,
      }),
    onSuccess: (r) => {
      setKeyResult(r);
      qc.invalidateQueries({ queryKey: ["keys"] });
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-1 text-sm text-ink-subtle">
        <button onClick={() => navigate("/connectors")} className="hover:text-ink">
          Connections
        </button>{" "}
        › <span className="text-ink">New connection</span>
      </div>

      <div className="card overflow-hidden">
        <Stepper steps={steps} current={stepIdx} />

        <div className="px-6 py-6">
          {step === "cloud" && (
            <Section
              title="Where does your storage live?"
              sub="BlobShip brokers uploads straight into your own cloud — your file bytes never touch our servers."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <CloudCard
                  selected={cloud === "azure"}
                  onClick={() => chooseCloud("azure")}
                  name="Microsoft Azure"
                  detail="Blob Storage · user-delegation SAS"
                />
                <CloudCard
                  selected={cloud === "aws"}
                  onClick={() => chooseCloud("aws")}
                  name="Amazon Web Services"
                  detail="S3 · assume-role + presigned URLs"
                />
              </div>
            </Section>
          )}

          {step === "account" && cloud === "azure" && (
            <Section
              title="Which storage account?"
              sub="Choose the Azure tenancy you've connected BlobShip to, then the storage account within it."
            >
              {consent === "ok" && (
                <div className="rounded-md border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">
                  ✓ Tenancy connected. Pick it below and continue.
                </div>
              )}
              {consent === "error" && (
                <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                  Consent didn't complete: {params.get("reason") ?? "unknown"}.
                </div>
              )}

              <Field label="Azure tenancy">
                {tenancies.data && tenancies.data.length > 0 ? (
                  <select className="input" value={tenancyId} onChange={(e) => setTenancyId(e.target.value)}>
                    <option value="" disabled>Select a connected tenancy…</option>
                    {tenancies.data.map((t) => (
                      <option key={t.id} value={t.id} disabled={!t.ready}>
                        {t.displayName}{t.ready ? "" : " (pending)"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-ink-muted">No tenancies connected yet.</p>
                )}
                <a href={api.azure.consentStartUrl()} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
                  Connect a tenancy
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></svg>
                </a>
                <p className="mt-1 text-xs text-ink-subtle">
                  Opens Microsoft admin consent — a one-time approval that lets BlobShip be granted access in that tenant.
                </p>
                {tenancies.data?.some((t) => !t.ready) && (
                  <p className="mt-1 text-xs text-warn">
                    A just-connected tenancy takes a few seconds to become ready — this updates automatically.
                  </p>
                )}
              </Field>

              <Field label="Display name">
                <input className="input" placeholder="e.g. Acme prod storage" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>
              <Field label="Storage account">
                <input className="input" placeholder="acmestorage" value={storageAccount} onChange={(e) => setStorageAccount(e.target.value)} />
              </Field>
              <MutError m={createConnector} fallback="Could not save the connection." />
              <Footer onBack={back} onNext={() => createConnector.mutate()} nextLabel="Continue" nextDisabled={!displayName || !tenancyId || !storageAccount || createConnector.isPending} busy={createConnector.isPending} />
            </Section>
          )}

          {step === "account" && cloud === "aws" && (
            <Section
              title="Connect your AWS account"
              sub="Name the connection and pick a region. Next you'll launch a one-click CloudFormation stack that grants BlobShip least-privilege access — no keys leave your account."
            >
              <Field label="Display name">
                <input className="input" placeholder="e.g. Acme prod storage" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>
              <Field label="Region">
                <input className="input" placeholder="eu-west-2" value={region} onChange={(e) => setRegion(e.target.value)} />
              </Field>

              <div className="rounded-md border border-surface-200 bg-surface-50 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium text-ink-muted">Your external ID (baked into the stack — keep for reference)</span>
                  <CopyButton text={externalId} className="btn px-2 py-0.5 text-xs text-accent hover:bg-accent-soft" />
                </div>
                <code className="mono block break-all text-sm text-ink">{externalId}</code>
              </div>

              <div>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink"
                  onClick={() => setShowAwsManual((v) => !v)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showAwsManual ? "rotate-90" : ""}`}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                  Prefer to create the role yourself? Paste an ARN
                </button>
                {showAwsManual && (
                  <div className="mt-3 rounded-[10px] border border-surface-200 bg-surface-100 px-3.5 py-3">
                    <p className="mb-2 text-xs text-ink-subtle">
                      Create a role that trusts BlobShip's minter with the external ID above and allows s3:PutObject into your bucket, then paste its ARN.
                    </p>
                    <Field label="Role ARN">
                      <input className="input mono" placeholder="arn:aws:iam::123456789012:role/blobship" value={roleArn} onChange={(e) => setRoleArn(e.target.value)} />
                    </Field>
                  </div>
                )}
              </div>

              <MutError m={createConnector} fallback="Could not save the connection." />
              <Footer onBack={back} onNext={() => createConnector.mutate()} nextLabel="Continue" nextDisabled={!displayName || !region || createConnector.isPending} busy={createConnector.isPending} />
            </Section>
          )}

          {step === "grant" && cloud === "azure" && (
            <Section
              title="Grant BlobShip access"
              sub="This grants BlobShip the least-privilege Storage Blob Data Contributor role on this account only — no subscription access, no keys. Revoke anytime."
            >
              <ol className="space-y-3 text-sm text-ink-muted">
                <li>
                  <span className="font-medium text-ink">1. Open the grant template</span>
                  <div className="mt-1.5">
                    {selectedTenancy?.spObjectId ? (
                      <a
                        href={azureDeployUrl({ principalId: selectedTenancy.spObjectId })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="btn-primary inline-flex items-center gap-2">
                          Grant access in Azure
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></svg>
                        </span>
                      </a>
                    ) : (
                      <span className="text-ink-subtle">Connect a tenancy first.</span>
                    )}
                  </div>
                </li>
                <li>
                  <span className="font-medium text-ink">2. Pick the subscription and storage account</span>, then Deploy.
                </li>
                <li><span className="font-medium text-ink">3. Come back here</span> and continue.</li>
              </ol>
              <Footer onBack={back} onNext={next} nextLabel="I've granted access →" />
            </Section>
          )}

          {step === "grant" && cloud === "aws" && (
            <Section
              title="Grant BlobShip access"
              sub="Launch a one-click CloudFormation stack in your AWS account. It creates a least-privilege role scoped to your bucket and registers it back automatically — nothing to paste."
            >
              <div className="rounded-md border border-surface-200 bg-surface-50 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium text-ink-muted">External ID (baked into the stack — keep for reference)</span>
                  <CopyButton text={externalId} className="btn px-2 py-0.5 text-xs text-accent hover:bg-accent-soft" />
                </div>
                <code className="mono block break-all text-sm text-ink">{externalId}</code>
              </div>

              {/* The stack's bucket and BlobShip's destination bucket are entered
                  separately and MUST match, so ask for it here and prefill the
                  stack with it. Left blank the template used to render its policy
                  resource as `arn:aws:s3:::/*` — matching nothing — and the stack
                  still "succeeded", producing a healthy-looking connector that
                  denied every upload. The template now refuses to deploy without
                  a bucket, and this field means the customer types it once. */}
              <Field label="Intake bucket">
                <input
                  className="input"
                  placeholder="acme-intake"
                  value={container}
                  onChange={(e) => setContainer(e.target.value)}
                />
                <p className="mt-1 text-xs text-ink-subtle">
                  We prefill the stack with this, and reuse it as the destination on
                  the next step — so the role's policy and the destination can't drift
                  apart.
                </p>
              </Field>

              <ol className="space-y-3 text-sm text-ink-muted">
                <li>
                  <span className="font-medium text-ink">1. Launch the stack</span>
                  <div className="mt-1.5">
                    {connector && container ? (
                      <a
                        href={awsDeployUrl({
                          region: region || "eu-west-2",
                          externalId,
                          nonce: connector.id,
                          callbackUrl: `${PUBLIC_API_URL}/api/connectors/register`,
                          bucket: container,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="btn-primary inline-flex items-center gap-2">
                          Launch CloudFormation stack
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></svg>
                        </span>
                      </a>
                    ) : (
                      <span className="text-ink-subtle">
                        {connector
                          ? "Enter the intake bucket above to continue."
                          : "Save the connection first."}
                      </span>
                    )}
                  </div>
                </li>
                <li>
                  <span className="font-medium text-ink">2. Review and create the stack</span> in the AWS console — the bucket is already filled in. If the bucket is encrypted with your own KMS key, paste that key's ARN into <code className="mono text-xs">KmsKeyArn</code>, or uploads will be denied.
                </li>
                <li>
                  <span className="font-medium text-ink">3. Come back here</span> — we detect the role automatically.
                </li>
              </ol>

              <div className={`rounded-md border px-3 py-2 text-sm ${awsRoleRegistered ? "border-ok/30 bg-ok-soft text-ok" : "border-surface-200 bg-surface-100 text-ink-muted"}`}>
                {awsRoleRegistered
                  ? "✓ Role detected — you're connected."
                  : "Waiting for the stack to finish and register the role… (this updates automatically)"}
              </div>

              <Footer onBack={back} onNext={next} nextLabel="Continue" />
            </Section>
          )}

          {step === "destination" && (
            <Section title="Where should files land?" sub={`Pick the ${storeNoun} and how teammates' files are organised.`}>
              <Field label={StoreNoun}>
                <input
                  className="input"
                  placeholder={cloud === "aws" ? "acme-intake" : "uploads"}
                  list="bs-containers"
                  value={container}
                  onChange={(e) => setContainer(e.target.value)}
                />
                <datalist id="bs-containers">
                  {(containerOptions.data ?? []).map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                {(containerOptions.data?.length ?? 0) > 0 && (
                  <p className="mt-1 text-xs text-ink-subtle">
                    {containerOptions.data!.length} container(s) found in this account — pick one or type a new name.
                  </p>
                )}
              </Field>
              <Field label="Path prefix (optional)">
                <input className="input mono" placeholder="inbox/" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
              </Field>
              <ToggleRow
                on={isolate}
                onToggle={() => setIsolate((v) => !v)}
                title="Keep each person's files private"
                sub="Everyone gets an isolated folder (prefix/their-email/…). A teammate's key can't read or send to anyone else's — admins keep oversight via Activity."
              />
              <div>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink"
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform ${showAdvanced ? "rotate-90" : ""}`}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                  Advanced options
                </button>
                {showAdvanced && (
                  <div className="mt-3 space-y-3 rounded-[10px] border border-surface-200 bg-surface-100 px-3.5 py-3">
                    <div>
                      <span className="label">Object naming</span>
                      <NamingSelect value={naming} onChange={setNaming} />
                    </div>
                    <div>
                      <span className="label">Max object size (MB) <span className="text-ink-subtle">(optional)</span></span>
                      <input
                        type="number"
                        min="1"
                        className="input max-w-[200px]"
                        placeholder="500"
                        value={maxMb}
                        onChange={(e) => setMaxMb(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
              <MutError m={createDestination} fallback="Could not save the destination." />
              <Footer onBack={back} onNext={() => createDestination.mutate()} nextLabel="Continue to key" nextDisabled={!container || createDestination.isPending} busy={createDestination.isPending} />
            </Section>
          )}

          {step === "key" && (
            <Section
              title={keyResult ? "Your upload key" : "Create an upload key"}
              sub={keyResult ? "Copy it now — for security we don't store the plaintext and can't show it again." : "This key belongs to you and is scoped to this destination. Each teammate creates their own."}
            >
              {!keyResult && (
                <AccessNote
                  probe={accessProbe}
                  exhausted={probeAttemptCount >= 90}
                  onRetry={() => {
                    // Restart the ~15-min poll from zero: reset the query (clears
                    // its update count so the interval resumes) and the exhausted
                    // counter. Lets a user who just fixed a grant re-check without
                    // reloading the page.
                    setProbeAttemptCount(0);
                    qc.resetQueries({
                      queryKey: ["accessProbe", connector?.id, destinationId],
                    });
                  }}
                  onRegrant={() => {
                    const i = steps.findIndex((s) => s.key === "grant");
                    if (i >= 0) setStepIdx(i);
                  }}
                />
              )}
              {!keyResult ? (
                <>
                  <MutError m={createKey} fallback="Could not create the key." />
                  {accessConfirmed && !createKey.isError ? (
                    // Access verified — minting automatically, no click needed.
                    <p className="text-sm text-ink-muted">Generating your key…</p>
                  ) : (
                    // Still polling for access (RBAC lag) or it failed — let the
                    // user mint by hand without waiting for the probe to go green.
                    <div className="flex items-center justify-between pt-2">
                      <button className="btn-secondary" onClick={back}>← Back</button>
                      <button
                        className="btn-primary"
                        onClick={() => createKey.mutate()}
                        disabled={createKey.isPending}
                      >
                        {createKey.isPending ? "Working…" : "Create my key anyway"}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="label mb-0">Plaintext key</span>
                      <CopyButton text={keyResult.plaintext} className="btn px-2 py-0.5 text-xs text-accent hover:bg-accent-soft" />
                    </div>
                    <code className="mono block break-all rounded-md border border-surface-200 bg-surface-50 px-3 py-2 text-sm text-ink">
                      {keyResult.plaintext}
                    </code>
                  </div>
                  <div>
                    <span className="label">Ready-to-paste command</span>
                    <CommandBlock unix={keyResult.oneLiner.curl} windows={keyResult.oneLiner.curlExe} />
                  </div>
                  <div className="flex justify-end">
                    <button className="btn-primary" onClick={() => navigate("/keys")}>Done</button>
                  </div>
                </div>
              )}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- presentational helpers ---------------------------------------------- */

function CloudCard({
  selected,
  onClick,
  name,
  detail,
}: {
  selected: boolean;
  onClick: () => void;
  name: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left transition-colors ${
        selected
          ? "border-accent bg-accent-soft/40 shadow-[0_0_0_3px_#1C1740]"
          : "border-surface-200 bg-surface-100 hover:border-surface-300"
      }`}
    >
      <div className="text-sm font-semibold text-ink">{name}</div>
      <div className="mt-0.5 text-xs text-ink-subtle">{detail}</div>
    </button>
  );
}

function Stepper({
  steps,
  current,
}: {
  steps: { key: StepKey; label: string }[];
  current: number;
}) {
  return (
    <div className="flex items-center border-b border-surface-200 px-6 py-5">
      {steps.map((s, i) => (
        <div key={s.key} className="flex flex-1 items-center last:flex-none">
          <span
            className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full text-xs font-bold ${
              i < current
                ? "bg-ok-soft text-ok"
                : i === current
                  ? "bg-accent text-white shadow-[0_0_0_4px_#1C1740]"
                  : "border border-surface-300 text-ink-subtle"
            }`}
          >
            {i < current ? "✓" : i + 1}
          </span>
          <span className={`ml-2.5 whitespace-nowrap text-[12.5px] font-semibold ${i <= current ? "text-ink" : "text-ink-muted"}`}>
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <span className={`mx-3 h-px flex-1 ${i < current ? "bg-ok" : "bg-surface-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      <p className="mb-5 mt-0.5 text-sm text-ink-muted">{sub}</p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="label">{label}</span>
      {children}
    </div>
  );
}

function ToggleRow({ on, onToggle, title, sub }: { on: boolean; onToggle: () => void; title: string; sub: string }) {
  return (
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-4 rounded-[10px] border border-surface-200 bg-surface-100 px-3.5 py-3 text-left">
      <span>
        <span className="block text-[13px] font-semibold text-ink">{title}</span>
        <span className="mt-0.5 block text-xs text-ink-subtle">{sub}</span>
      </span>
      <span className={`relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors ${on ? "bg-accent" : "bg-surface-300"}`}>
        <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[19px]" : "left-[3px]"}`} />
      </span>
    </button>
  );
}

/**
 * The access-check panel on the key step.
 *
 * This is the exact surface that once printed raw Azure XML
 * (`<Code>AuthorizationFailure</Code>…`) at a customer, who reasonably read it as
 * "BlobShip is broken". It now renders only the API's customer-safe diagnosis:
 * a headline, one next step, and the provider reference tucked behind a
 * disclosure for support. Tone follows `fault` — reassure while a grant settles,
 * apologise when it's our fault, give a concrete action when it's theirs.
 */
function AccessNote({
  probe,
  exhausted,
  onRetry,
  onRegrant,
}: {
  probe: UseQueryResult<TestConnectorResult>;
  exhausted: boolean;
  /** Restart the poll loop — shown once we've stopped waiting. */
  onRetry?: () => void;
  /** Jump back to the grant step — for a user who advanced before the grant
   *  actually finished deploying, or who needs to re-grant. */
  onRegrant?: () => void;
}) {
  const data = probe.data;

  if (data?.ok) {
    return (
      <div className="rounded-md border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">
        ✓ Access verified.
      </div>
    );
  }

  // First tick: no verdict yet.
  if (!data) {
    return (
      <div className="rounded-md border border-surface-200 bg-surface-100 px-3 py-2 text-sm text-ink-muted">
        Checking access…
      </div>
    );
  }

  const d = data.diagnosis;
  const shown = exhausted && d.retryable
    ? {
        ...d,
        title: "Access is still not available.",
        nextStep:
          "The normal propagation window has passed. Check the grant scope and storage firewall or bucket policy, then use Test access after making a change. You can still create a key and try a real upload; that is definitive when an AWS policy treats BlobShip's probe IP differently from your device.",
        retryable: false,
      }
    : d;
  // Waiting on a cloud (theirs or ours) is neutral news; a final verdict that
  // needs someone to act is a warning. Nothing here is ever styled as a crash.
  const waiting = shown.retryable;
  const tone = waiting
    ? "border-surface-200 bg-surface-100 text-ink-muted"
    : "border-warn/30 bg-warn-soft text-warn";

  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${tone}`}>
      <div className="flex items-start gap-2">
        {waiting && (
          <span
            className="mt-[3px] h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60"
            aria-hidden="true"
          />
        )}
        <div>
          <span className="font-medium">{shown.title}</span>
          <span className="mt-0.5 block text-xs opacity-90">{shown.nextStep}</span>
          {shown.fault === "blobship" && (
            <span className="mt-1 block text-xs opacity-90">
              Sorry — this one is on us, not your Azure setup.
            </span>
          )}
          {shown.reference && (
            <details className="mt-1.5">
              <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">
                Technical details for support
              </summary>
              <code className="mono mt-1 block break-all text-[11px] opacity-80">
                {shown.code} · {shown.reference}
              </code>
            </details>
          )}
        </div>
      </div>
      {/* Once we've stopped waiting (a timeout or a final verdict), never leave
          the user at a dead end: let them re-check in place, or step back to the
          grant if they got ahead of the deployment. */}
      {!waiting && (onRetry || onRegrant) && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {onRetry && (
            <button type="button" className="btn-secondary" onClick={onRetry}>
              Check again
            </button>
          )}
          {onRegrant && (
            <button type="button" className="btn-secondary" onClick={onRegrant}>
              Back to grant access
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Footer({ onBack, onNext, nextLabel, nextDisabled, busy }: { onBack?: () => void; onNext: () => void; nextLabel: string; nextDisabled?: boolean; busy?: boolean }) {
  return (
    <div className="flex items-center justify-between pt-2">
      {onBack ? (
        <button className="btn-secondary" onClick={onBack}>← Back</button>
      ) : (
        <span />
      )}
      <button className="btn-primary" onClick={onNext} disabled={nextDisabled}>
        {busy ? "Working…" : nextLabel}
      </button>
    </div>
  );
}

function MutError({ m, fallback }: { m: { isError: boolean; error: unknown }; fallback: string }) {
  if (!m.isError) return null;
  return (
    <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
      {m.error instanceof Error ? m.error.message : fallback}
    </p>
  );
}
